import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  RefreshCw,
  Search,
  Users,
  CalendarRange,
  CalendarDays,
  X,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Check,
  Copy,
} from 'lucide-react';
import { AdminService, supabase } from '../../services/adminService';

const DEFAULT_MONTHLY_SALARY = 5000;

const STATUS_META = {
  present: { label: 'Present', short: 'P', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0', text: 'text-green-700', chip: 'bg-green-500' },
  halfday: { label: 'Halfday', short: 'H', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', text: 'text-yellow-700', chip: 'bg-yellow-500' },
  absent: { label: 'Absent', short: 'A', color: '#B91C1C', bg: '#FEF2F2', border: '#FCA5A5', text: 'text-red-700', chip: 'bg-red-500' },
  office_leave: { label: 'Office Leave', short: 'L', color: '#1D4ED8', bg: '#EFF6FF', border: '#93C5FD', text: 'text-blue-700', chip: 'bg-blue-500' },
};

const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const monthLabel = (d) =>
  d.toLocaleString('default', { month: 'long', year: 'numeric' });

// Sun–Sat single letters (matches S,M,T,W,T,F,S)
const DAY_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const formatTalkTime = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const talkCacheKey = (tcId, dateStr) => `${tcId}|${dateStr}`;

const formatDisplayDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const EmployeeAttendance = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('today'); // 'today' | 'register'
  const [selectedDate, setSelectedDate] = useState(toYMD(new Date()));
  const [historyMonth, setHistoryMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [search, setSearch] = useState('');
  const [tlFilter, setTlFilter] = useState(''); // '' = all
  const [tcStatusByDate, setTcStatusByDate] = useState({}); // { tcId: status }
  const [tcMonthHistory, setTcMonthHistory] = useState({}); // { tcId: { 'YYYY-MM-DD': status } }
  const [tlByTc, setTlByTc] = useState({}); // { tcId: { id, name } }
  const [statusLoading, setStatusLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openTc, setOpenTc] = useState(null); // { id, name } when modal open
  const [editCell, setEditCell] = useState(null); // { tcId, tcName, date, status, tlId }
  const [savingStatus, setSavingStatus] = useState(false);
  const [editError, setEditError] = useState('');
  const [talkSecondsByKey, setTalkSecondsByKey] = useState({}); // { 'tcId|date': number }
  const [talkLoadingKey, setTalkLoadingKey] = useState(null);
  const [hoverTip, setHoverTip] = useState(null); // { x, y, tcName, date, status, key }
  const [monthDeliveredByTc, setMonthDeliveredByTc] = useState({}); // { tcId: { count, revenue } }
  const [deliveredLoading, setDeliveredLoading] = useState(false);
  const [deliveredListModal, setDeliveredListModal] = useState(null); // { title, tcIds }
  const [deliveredCustomers, setDeliveredCustomers] = useState([]);
  const [deliveredCustomersLoading, setDeliveredCustomersLoading] = useState(false);
  const [deliveredCustomersError, setDeliveredCustomersError] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const { data: users = [], isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: AdminService.getAllUsers,
  });

  const telecallers = useMemo(() => {
    return (users || [])
      .filter((u) => Array.isArray(u.roles) && u.roles.includes('telecaller'))
      .filter((u) => {
        const s = String(u.status || 'active').toLowerCase();
        return s === 'active';
      })
      .map((u) => ({ id: String(u.id), name: u.name || u.email || 'Telecaller', email: u.email, phone: u.phone }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  // Best-effort: fetch each TC's TL once when TC list changes.
  useEffect(() => {
    let cancelled = false;
    const loadTls = async () => {
      const map = {};
      await Promise.all(
        telecallers.map(async (tc) => {
          try {
            const { data } = await supabase.rpc('get_teamlead_for_telecaller', { p_telecaller_id: String(tc.id) });
            const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
            if (row && row.team_lead_id) {
              map[tc.id] = { id: String(row.team_lead_id), name: row.team_lead_name || 'Team Lead' };
            }
          } catch {
            /* ignore individual failures */
          }
        })
      );
      if (!cancelled) setTlByTc(map);
    };
    if (telecallers.length) loadTls();
    return () => {
      cancelled = true;
    };
  }, [telecallers]);

  // Load attendance for selected date for all TCs (used in Today view)
  useEffect(() => {
    let cancelled = false;
    const loadDay = async () => {
      if (!telecallers.length) {
        setTcStatusByDate({});
        return;
      }
      setStatusLoading(true);
      const map = {};
      try {
        await Promise.all(
          telecallers.map(async (tc) => {
            try {
              const { data, error } = await supabase.rpc('tl_get_tc_attendance', {
                p_tc_id: tc.id,
                p_from: selectedDate,
                p_to: selectedDate,
              });
              if (error) return;
              const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
              if (row && row.status) map[tc.id] = String(row.status).toLowerCase();
            } catch {
              /* ignore */
            }
          })
        );
        if (!cancelled) setTcStatusByDate(map);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };
    if (view === 'today') loadDay();
    return () => {
      cancelled = true;
    };
  }, [telecallers, selectedDate, view]);

  // Load monthly register for all TCs (used in Register view)
  useEffect(() => {
    let cancelled = false;
    const loadMonth = async () => {
      if (!telecallers.length) {
        setTcMonthHistory({});
        return;
      }
      setRegisterLoading(true);
      const firstDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth(), 1);
      const lastDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0);
      const fromDate = toYMD(firstDay);
      const toDate = toYMD(lastDay);
      const map = {};
      try {
        await Promise.all(
          telecallers.map(async (tc) => {
            try {
              const { data, error } = await supabase.rpc('tl_get_tc_attendance', {
                p_tc_id: tc.id,
                p_from: fromDate,
                p_to: toDate,
              });
              if (error) {
                map[tc.id] = {};
                return;
              }
              const inner = {};
              (data || []).forEach((r) => {
                if (r && r.date) {
                  const key = String(r.date).slice(0, 10);
                  inner[key] = String(r.status || '').toLowerCase();
                }
              });
              map[tc.id] = inner;
            } catch {
              map[tc.id] = {};
            }
          })
        );
        if (!cancelled) setTcMonthHistory(map);
      } finally {
        if (!cancelled) setRegisterLoading(false);
      }
    };
    if (view === 'register') loadMonth();
    return () => {
      cancelled = true;
    };
  }, [telecallers, historyMonth, view]);

  // Delivered count + revenue for the open register month (per TC)
  useEffect(() => {
    let cancelled = false;
    const loadDelivered = async () => {
      if (view !== 'register' || !telecallers.length) {
        setMonthDeliveredByTc({});
        return;
      }
      setDeliveredLoading(true);
      const firstDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth(), 1);
      const lastDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0);
      const fromDate = toYMD(firstDay);
      const toDate = toYMD(lastDay);
      try {
        const { data, error } = await supabase
          .from('master_orders')
          .select('telecaller_id, final_amount')
          .in('current_status_type', ['delivered', 'DELIVERED'])
          .not('sr_status_raw_time', 'is', null)
          .gte('sr_status_raw_time', `${fromDate}T00:00:00`)
          .lte('sr_status_raw_time', `${toDate}T23:59:59`);
        if (error) throw error;
        const map = {};
        (data || []).forEach((row) => {
          const tcId = String(row.telecaller_id || '');
          if (!tcId) return;
          if (!map[tcId]) map[tcId] = { count: 0, revenue: 0 };
          map[tcId].count += 1;
          map[tcId].revenue += Number(row.final_amount) || 0;
        });
        if (!cancelled) setMonthDeliveredByTc(map);
      } catch (e) {
        console.error('Month delivered load failed:', e);
        if (!cancelled) setMonthDeliveredByTc({});
      } finally {
        if (!cancelled) setDeliveredLoading(false);
      }
    };
    loadDelivered();
    return () => {
      cancelled = true;
    };
  }, [telecallers, historyMonth, view]);

  const teamLeadOptions = useMemo(() => {
    const m = new Map();
    Object.values(tlByTc).forEach((v) => {
      if (v && v.id) m.set(v.id, v.name || 'Team Lead');
    });
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tlByTc]);

  const filteredTcs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return telecallers.filter((tc) => {
      const tl = tlByTc[tc.id];
      if (tlFilter && (!tl || tl.id !== tlFilter)) return false;
      if (!q) return true;
      return (
        tc.name.toLowerCase().includes(q) ||
        (tc.email || '').toLowerCase().includes(q) ||
        (tl?.name || '').toLowerCase().includes(q)
      );
    });
  }, [telecallers, search, tlFilter, tlByTc]);

  const counts = useMemo(() => {
    const c = { present: 0, halfday: 0, absent: 0, office_leave: 0, not_marked: 0, total: filteredTcs.length };
    filteredTcs.forEach((tc) => {
      const s = tcStatusByDate[tc.id];
      if (s && c[s] !== undefined) c[s] += 1;
      else c.not_marked += 1;
    });
    return c;
  }, [filteredTcs, tcStatusByDate]);

  // Sum of payable salary for all visible TCs in the open register month
  const monthSalaryTotal = useMemo(() => {
    const totalDays = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0).getDate();
    return filteredTcs.reduce(
      (sum, tc) => sum + calcMonthSalary(tcMonthHistory[tc.id], totalDays),
      0
    );
  }, [filteredTcs, tcMonthHistory, historyMonth]);

  const monthDeliveredStats = useMemo(() => {
    let count = 0;
    let revenue = 0;
    filteredTcs.forEach((tc) => {
      const row = monthDeliveredByTc[tc.id];
      if (!row) return;
      count += row.count;
      revenue += row.revenue;
    });
    return { count, revenue };
  }, [filteredTcs, monthDeliveredByTc]);

  const openDeliveredCustomers = (opts) => {
    const tcIds = (opts?.tcIds || []).map(String).filter(Boolean);
    setDeliveredListModal({
      title: opts?.title || `Delivered · ${monthLabel(historyMonth)}`,
      tcIds,
    });
    setDeliveredCustomers([]);
    setDeliveredCustomersError('');
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!deliveredListModal) return;
      const tcIds = deliveredListModal.tcIds || [];
      if (!tcIds.length) {
        setDeliveredCustomers([]);
        return;
      }
      setDeliveredCustomersLoading(true);
      setDeliveredCustomersError('');
      try {
        const firstDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth(), 1);
        const lastDay = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0);
        const fromDate = toYMD(firstDay);
        const toDate = toYMD(lastDay);
        const { data, error } = await supabase
          .from('master_orders')
          .select(
            'order_id, telecaller_id, customer_name, contact_number, final_amount, order_created_at, sr_status_raw_time, courier_name'
          )
          .in('telecaller_id', tcIds)
          .in('current_status_type', ['delivered', 'DELIVERED'])
          .not('sr_status_raw_time', 'is', null)
          .gte('sr_status_raw_time', `${fromDate}T00:00:00`)
          .lte('sr_status_raw_time', `${toDate}T23:59:59`)
          .order('sr_status_raw_time', { ascending: false });
        if (error) throw error;
        if (!cancelled) setDeliveredCustomers(data || []);
      } catch (e) {
        console.error('Delivered customers load failed:', e);
        if (!cancelled) {
          setDeliveredCustomers([]);
          setDeliveredCustomersError(e?.message || 'Customer list load fail ho gaya');
        }
      } finally {
        if (!cancelled) setDeliveredCustomersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [deliveredListModal, historyMonth]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchUsers();
    } finally {
      setRefreshing(false);
    }
  };

  const ensureTalkTime = async (tcId, dateStr) => {
    const key = talkCacheKey(tcId, dateStr);
    if (talkSecondsByKey[key] !== undefined) return talkSecondsByKey[key];
    setTalkLoadingKey(key);
    try {
      const { data, error } = await supabase.rpc('tc_total_talk_seconds_by_date', {
        p_tc_id: tcId,
        p_date: dateStr,
      });
      if (error) throw error;
      const seconds = Number(data) || 0;
      setTalkSecondsByKey((prev) => ({ ...prev, [key]: seconds }));
      return seconds;
    } catch (e) {
      console.warn('Talk time load failed:', e);
      setTalkSecondsByKey((prev) => ({ ...prev, [key]: 0 }));
      return 0;
    } finally {
      setTalkLoadingKey((prev) => (prev === key ? null : prev));
    }
  };

  const openEditCell = (tc, dateStr, currentStatus) => {
    const tl = tlByTc[tc.id];
    setEditError('');
    setEditCell({
      tcId: tc.id,
      tcName: tc.name,
      date: dateStr,
      status: currentStatus || null,
      tlId: tl?.id || currentUser?.id || null,
    });
    ensureTalkTime(tc.id, dateStr);
  };

  const handleSaveAttendance = async (status) => {
    if (!editCell || !status) return;
    const tlId = editCell.tlId || currentUser?.id;
    if (!tlId) {
      setEditError('TL / admin id nahi mila. Dobara login karke try karein.');
      return;
    }
    setSavingStatus(true);
    setEditError('');
    try {
      const { error } = await supabase.rpc('tl_mark_tc_attendance', {
        p_tl_id: tlId,
        p_tc_id: editCell.tcId,
        p_date: editCell.date,
        p_status: status,
        p_notes: 'Manual update by admin',
      });
      if (error) throw error;

      setTcMonthHistory((prev) => ({
        ...prev,
        [editCell.tcId]: {
          ...(prev[editCell.tcId] || {}),
          [editCell.date]: status,
        },
      }));
      if (editCell.date === selectedDate) {
        setTcStatusByDate((prev) => ({ ...prev, [editCell.tcId]: status }));
      }
      setEditCell(null);
    } catch (e) {
      console.error('Mark attendance failed:', e);
      setEditError(e?.message || 'Attendance update fail ho gaya');
    } finally {
      setSavingStatus(false);
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
          Not Marked
        </span>
      );
    }
    const meta = STATUS_META[status] || STATUS_META.absent;
    return (
      <span className={`px-3 py-1 text-xs font-semibold text-white rounded-full ${meta.chip}`}>
        {meta.label}
      </span>
    );
  };

  const renderTodayView = () => {
    return (
      <div className="space-y-6">
        {/* Date selector + summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={toYMD(new Date())}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={() => setSelectedDate(toYMD(new Date()))}
              disabled={selectedDate === toYMD(new Date())}
              className="px-3 py-2 text-sm font-semibold border border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Today
            </button>
            <div className="ml-auto text-sm text-gray-500">
              {filteredTcs.length} telecaller{filteredTcs.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Present" value={counts.present} icon={CheckCircle} colorClass="text-green-600" />
            <SummaryCard label="Halfday" value={counts.halfday} icon={Clock} colorClass="text-yellow-600" />
            <SummaryCard label="Absent" value={counts.absent} icon={XCircle} colorClass="text-red-600" />
            <SummaryCard label="Office Leave" value={counts.office_leave} icon={AlertCircle} colorClass="text-blue-600" />
            <SummaryCard label="Not Marked" value={counts.not_marked} icon={Users} colorClass="text-gray-500" />
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {statusLoading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
              <p className="text-gray-600">Attendance load ho rahi hai…</p>
            </div>
          ) : filteredTcs.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={48} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Koi telecaller nahi mila</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTcs.map((tc) => {
                const status = tcStatusByDate[tc.id];
                const tl = tlByTc[tc.id];
                const meta = status ? STATUS_META[status] : null;
                return (
                  <button
                    key={tc.id}
                    type="button"
                    onClick={() => setOpenTc({ id: tc.id, name: tc.name, tlName: tl?.name, phone: tc.phone })}
                    className="w-full flex items-center gap-4 p-4 hover:bg-primary-50 transition text-left"
                    style={meta ? { borderLeft: `4px solid ${meta.color}` } : { borderLeft: '4px solid transparent' }}
                  >
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-primary-600 font-bold">
                        {(tc.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate hover:text-primary-600">{tc.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {tc.email || tc.phone || '—'}
                        {tl ? <>  •  TL: <span className="font-medium text-gray-700">{tl.name}</span></> : null}
                      </p>
                    </div>
                    <div>{renderStatusBadge(status)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRegisterView = () => {
    const daysInMonth = new Date(historyMonth.getFullYear(), historyMonth.getMonth() + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Attendance Register</h3>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => {
                const d = new Date(historyMonth);
                d.setMonth(d.getMonth() - 1);
                setHistoryMonth(d);
              }}
              className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
            >
              ‹
            </button>
            <span className="px-3 text-sm font-semibold text-gray-900 min-w-[140px] text-center">
              {monthLabel(historyMonth)}
            </span>
            <button
              onClick={() => {
                const d = new Date(historyMonth);
                d.setMonth(d.getMonth() + 1);
                setHistoryMonth(d);
              }}
              className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
            >
              ›
            </button>
          </div>
        </div>

        {registerLoading ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Register load ho raha hai…</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-xs">
                <thead className="bg-indigo-600 text-white sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-indigo-600 z-10 w-56 min-w-[220px]">
                      Telecaller
                    </th>
                    {days.map((d) => {
                      const weekday = new Date(
                        historyMonth.getFullYear(),
                        historyMonth.getMonth(),
                        d
                      ).getDay();
                      return (
                        <th key={d} className="px-1.5 py-1.5 text-center font-semibold w-9 leading-tight">
                          <div>{d}</div>
                          <div className="text-[10px] font-medium text-white/80">{DAY_LETTER[weekday]}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredTcs.length === 0 ? (
                    <tr>
                      <td colSpan={days.length + 1} className="p-8 text-center text-gray-500">
                        Koi telecaller nahi mila
                      </td>
                    </tr>
                  ) : (
                    filteredTcs.map((tc) => {
                      const tl = tlByTc[tc.id];
                      const inner = tcMonthHistory[tc.id] || {};
                      const salary = calcMonthSalary(inner, daysInMonth);
                      const delivered = monthDeliveredByTc[tc.id] || { count: 0, revenue: 0 };
                      return (
                        <tr key={tc.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 sticky left-0 bg-white border-r border-gray-100 z-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() => setOpenTc({ id: tc.id, name: tc.name, tlName: tl?.name, phone: tc.phone })}
                                  className="text-left hover:text-primary-600"
                                  title="View attendance summary"
                                >
                                  <div className="font-semibold text-gray-900 leading-tight hover:text-primary-600 truncate">{tc.name}</div>
                                  {tl ? <div className="text-[11px] text-gray-500 leading-tight">{tl.name}</div> : null}
                                </button>
                                <div className="text-[10px] text-gray-500 leading-tight mt-0.5">
                                  <span className="text-sky-700 font-semibold">{delivered.count}</span>
                                  {' del · '}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openDeliveredCustomers({
                                        title: `${tc.name} · Delivered · ${monthLabel(historyMonth)}`,
                                        tcIds: [tc.id],
                                      })
                                    }
                                    className="text-amber-700 font-semibold underline decoration-amber-300 hover:text-amber-900"
                                    title="View delivered customers"
                                  >
                                    ₹{Math.round(delivered.revenue).toLocaleString('en-IN')}
                                  </button>
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-[11px] font-bold text-emerald-700 leading-tight">
                                  ₹{salary.toLocaleString('en-IN')}
                                </div>
                                <div className="text-[9px] text-gray-400 leading-tight">salary</div>
                              </div>
                            </div>
                          </td>
                          {days.map((d) => {
                            const dateStr = `${historyMonth.getFullYear()}-${String(historyMonth.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                            const s = inner[dateStr];
                            const meta = s ? STATUS_META[s] : null;
                            const tKey = talkCacheKey(tc.id, dateStr);
                            return (
                              <td key={d} className="p-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setHoverTip(null);
                                    openEditCell(tc, dateStr, s);
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHoverTip({
                                      x: rect.left + rect.width / 2,
                                      y: rect.bottom + 6,
                                      tcName: tc.name,
                                      date: dateStr,
                                      status: s || null,
                                      key: tKey,
                                    });
                                    ensureTalkTime(tc.id, dateStr);
                                  }}
                                  onMouseLeave={() =>
                                    setHoverTip((prev) => (prev?.key === tKey ? null : prev))
                                  }
                                  onFocus={() => ensureTalkTime(tc.id, dateStr)}
                                  className="w-full h-full min-h-[28px] text-center font-bold hover:ring-2 hover:ring-primary-400 hover:ring-inset focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  style={{
                                    backgroundColor: meta ? meta.bg : '#fafafa',
                                    color: meta ? meta.color : '#cbd5e1',
                                  }}
                                >
                                  {meta ? meta.short : '–'}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-4 items-center pt-3 border-t border-gray-100">
              <span className="text-sm font-semibold text-gray-700">Legend:</span>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <div key={k} className="flex items-center gap-2 text-sm">
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded font-bold text-xs border"
                    style={{ backgroundColor: m.bg, color: m.color, borderColor: m.border }}
                  >
                    {m.short}
                  </span>
                  <span className="text-gray-700">{m.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded font-bold text-xs border bg-gray-50 text-gray-300 border-gray-200">
                  –
                </span>
                <span className="text-gray-700">Not Marked</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/more')}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Back to More Options"
          >
            <ArrowLeft size={22} className="text-gray-700" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Employee Attendance</h1>
            <p className="text-gray-600 mt-1">
              {view === 'today'
                ? `Attendance for ${selectedDate}`
                : `Monthly register for ${monthLabel(historyMonth)}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {view === 'register' && (
            <>
              <div className="px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 text-right min-w-[120px]">
                <div className="text-[11px] font-medium text-sky-700 leading-none">
                  Delivered · {monthLabel(historyMonth)}
                </div>
                <div className="text-lg font-bold text-sky-800 mt-1 leading-none">
                  {deliveredLoading ? '…' : monthDeliveredStats.count.toLocaleString('en-IN')}
                </div>
                <div className="text-[10px] text-sky-600/80 mt-1">orders</div>
              </div>
              <button
                type="button"
                onClick={() =>
                  openDeliveredCustomers({
                    title: `All TCs · Delivered · ${monthLabel(historyMonth)}`,
                    tcIds: filteredTcs.map((tc) => tc.id),
                  })
                }
                className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-right min-w-[130px] hover:bg-amber-100 transition"
                title="View delivered customers"
              >
                <div className="text-[11px] font-medium text-amber-700 leading-none">
                  Revenue · {monthLabel(historyMonth)}
                </div>
                <div className="text-lg font-bold text-amber-800 mt-1 leading-none">
                  {deliveredLoading
                    ? '…'
                    : `₹${Math.round(monthDeliveredStats.revenue).toLocaleString('en-IN')}`}
                </div>
                <div className="text-[10px] text-amber-600/80 mt-1">click for list</div>
              </button>
              <div className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-right min-w-[140px]">
                <div className="text-[11px] font-medium text-emerald-700 leading-none">
                  Total salary · {monthLabel(historyMonth)}
                </div>
                <div className="text-lg font-bold text-emerald-800 mt-1 leading-none">
                  {registerLoading ? '…' : `₹${monthSalaryTotal.toLocaleString('en-IN')}`}
                </div>
                <div className="text-[10px] text-emerald-600/80 mt-1">
                  {filteredTcs.length} TC{filteredTcs.length === 1 ? '' : 's'}
                </div>
              </div>
            </>
          )}
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('today')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                view === 'today' ? 'bg-white text-primary-600 shadow' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <CalendarDays size={16} />
              Daily
            </button>
            <button
              onClick={() => setView('register')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                view === 'register' ? 'bg-white text-primary-600 shadow' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <CalendarRange size={16} />
              Monthly Register
            </button>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || isLoadingUsers}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span className="text-sm">{refreshing ? 'Refreshing…' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or team lead…"
            className="flex-1 outline-none text-sm bg-transparent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-xs text-gray-500 hover:text-gray-700">
              Clear
            </button>
          )}
        </div>
        <div>
          <select
            value={tlFilter}
            onChange={(e) => setTlFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Team Leads</option>
            {teamLeadOptions.map((tl) => (
              <option key={tl.id} value={tl.id}>
                {tl.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoadingUsers ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Employees load ho rahe hain…</p>
        </div>
      ) : view === 'today' ? (
        renderTodayView()
      ) : (
        renderRegisterView()
      )}

      {openTc && (
        <TcAttendanceModal
          key={`${openTc.id}-${view === 'register' ? toYMD(historyMonth).slice(0, 7) : selectedDate.slice(0, 7)}`}
          tc={openTc}
          initialMonth={
            view === 'register'
              ? historyMonth
              : (() => {
                  const d = new Date(`${selectedDate}T00:00:00`);
                  return new Date(d.getFullYear(), d.getMonth(), 1);
                })()
          }
          onClose={() => setOpenTc(null)}
        />
      )}

      {hoverTip && (
        <div
          className="fixed z-[70] pointer-events-none px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-[11px] shadow-lg max-w-[240px] -translate-x-1/2"
          style={{ left: hoverTip.x, top: hoverTip.y }}
        >
          <div className="font-semibold truncate">{hoverTip.tcName}</div>
          <div className="text-white/80">
            {hoverTip.date}
            {' · '}
            {hoverTip.status ? STATUS_META[hoverTip.status]?.label || hoverTip.status : 'Not Marked'}
          </div>
          <div className="text-violet-200 font-semibold mt-0.5">
            Talk Time:{' '}
            {talkSecondsByKey[hoverTip.key] !== undefined
              ? formatTalkTime(talkSecondsByKey[hoverTip.key])
              : talkLoadingKey === hoverTip.key
                ? '…'
                : '—'}
          </div>
          <div className="text-white/60 mt-0.5">Click to change</div>
        </div>
      )}

      {deliveredListModal && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDeliveredListModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{deliveredListModal.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {deliveredCustomersLoading
                    ? 'Loading…'
                    : `${deliveredCustomers.length} customer${deliveredCustomers.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDeliveredListModal(null)}
                className="p-1 hover:bg-gray-100 rounded-lg shrink-0"
                title="Close"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>
            <div className="overflow-auto p-4">
              {deliveredCustomersLoading ? (
                <div className="py-10 text-center">
                  <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-3"></div>
                  <p className="text-sm text-gray-600">Customers load ho rahe hain…</p>
                </div>
              ) : deliveredCustomersError ? (
                <p className="text-sm text-red-600">{deliveredCustomersError}</p>
              ) : deliveredCustomers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Is month mein koi delivered customer nahi mila</p>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">#</th>
                        <th className="px-3 py-2 text-left font-semibold">Customer</th>
                        <th className="px-3 py-2 text-left font-semibold">Number</th>
                        <th className="px-3 py-2 text-right font-semibold">Amount</th>
                        <th className="px-3 py-2 text-left font-semibold">Courier</th>
                        <th className="px-3 py-2 text-left font-semibold">Lead Confirm</th>
                        <th className="px-3 py-2 text-left font-semibold">Delivery</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveredCustomers.map((row, idx) => (
                        <tr key={row.order_id || `${row.contact_number}-${idx}`} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {row.customer_name || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {row.contact_number || '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-amber-700 whitespace-nowrap">
                            ₹{Math.round(Number(row.final_amount) || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {row.courier_name || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {formatDisplayDate(row.order_created_at)}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {formatDisplayDate(row.sr_status_raw_time)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editCell && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !savingStatus && setEditCell(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">Change attendance</h3>
                <p className="text-sm text-gray-600 mt-0.5 truncate">{editCell.tcName}</p>
                <p className="text-xs text-gray-500 mt-0.5">{editCell.date}</p>
                <p className="text-xs font-semibold text-violet-700 mt-1">
                  Talk Time:{' '}
                  {talkSecondsByKey[talkCacheKey(editCell.tcId, editCell.date)] !== undefined
                    ? formatTalkTime(talkSecondsByKey[talkCacheKey(editCell.tcId, editCell.date)])
                    : talkLoadingKey === talkCacheKey(editCell.tcId, editCell.date)
                      ? '…'
                      : '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !savingStatus && setEditCell(null)}
                className="p-1 hover:bg-gray-100 rounded-lg shrink-0"
                title="Close"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-xs text-gray-500 mb-1">
                Current:{' '}
                <span className="font-semibold text-gray-700">
                  {editCell.status ? STATUS_META[editCell.status]?.label || editCell.status : 'Not Marked'}
                </span>
              </p>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  disabled={savingStatus}
                  onClick={() => handleSaveAttendance(key)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left hover:bg-gray-50 disabled:opacity-50"
                  style={{
                    borderColor: editCell.status === key ? meta.border : '#E5E7EB',
                    backgroundColor: editCell.status === key ? meta.bg : undefined,
                  }}
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded font-bold text-xs border"
                    style={{ backgroundColor: meta.bg, color: meta.color, borderColor: meta.border }}
                  >
                    {meta.short}
                  </span>
                  <span className="font-semibold text-gray-900">{meta.label}</span>
                  {editCell.status === key ? (
                    <Check size={16} className="ml-auto text-gray-500" />
                  ) : null}
                </button>
              ))}
              {editError ? <p className="text-xs text-red-600 pt-1">{editError}</p> : null}
              {savingStatus ? <p className="text-xs text-gray-500 pt-1">Saving…</p> : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon, colorClass }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200 flex items-center justify-between">
    <div>
      <p className="text-xs text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
    <Icon size={22} className={colorClass} />
  </div>
);

const summarizeAttendance = (statusByDate, effectiveDays) => {
  const c = { present: 0, halfday: 0, absent: 0, office_leave: 0, marked: 0 };
  Object.values(statusByDate).forEach((s) => {
    if (c[s] !== undefined) c[s] += 1;
    c.marked += 1;
  });
  const notMarked = Math.max(0, effectiveDays - c.marked);
  const presentEquivalent = c.present + c.halfday * 0.5;
  const attendanceRate =
    effectiveDays > 0 ? Math.round((presentEquivalent / effectiveDays) * 100) : 0;
  return { ...c, notMarked, attendanceRate };
};

const calcMonthSalary = (statusByDate, totalDays, monthlySalary = DEFAULT_MONTHLY_SALARY) => {
  if (!totalDays) return 0;
  let present = 0;
  let halfday = 0;
  Object.values(statusByDate || {}).forEach((s) => {
    if (s === 'present') present += 1;
    else if (s === 'halfday') halfday += 1;
  });
  return Math.round((monthlySalary / totalDays) * (present + halfday * 0.5));
};

const TcAttendanceModal = ({ tc, initialMonth, onClose }) => {
  const [month, setMonth] = useState(() => {
    const d = initialMonth instanceof Date ? initialMonth : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [loading, setLoading] = useState(false);
  const [statusByDate, setStatusByDate] = useState({});
  const [currentMonthStatus, setCurrentMonthStatus] = useState({});
  const [currentMonthLoading, setCurrentMonthLoading] = useState(false);

  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const currentMonthKey = `${currentMonthStart.getFullYear()}-${currentMonthStart.getMonth()}`;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
        const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        const { data, error } = await supabase.rpc('tl_get_tc_attendance', {
          p_tc_id: tc.id,
          p_from: toYMD(firstDay),
          p_to: toYMD(lastDay),
        });
        if (error) throw error;
        const map = {};
        (data || []).forEach((r) => {
          if (r && r.date) {
            map[String(r.date).slice(0, 10)] = String(r.status || '').toLowerCase();
          }
        });
        if (!cancelled) setStatusByDate(map);
      } catch (e) {
        console.error('Modal load failed:', e);
        if (!cancelled) setStatusByDate({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tc.id, month]);

  // Always keep a small current-month snapshot for the compact strip
  useEffect(() => {
    let cancelled = false;
    const loadCurrent = async () => {
      setCurrentMonthLoading(true);
      try {
        const n = new Date();
        const firstDay = new Date(n.getFullYear(), n.getMonth(), 1);
        const lastDay = new Date(n.getFullYear(), n.getMonth() + 1, 0);
        const { data, error } = await supabase.rpc('tl_get_tc_attendance', {
          p_tc_id: tc.id,
          p_from: toYMD(firstDay),
          p_to: toYMD(lastDay),
        });
        if (error) throw error;
        const map = {};
        (data || []).forEach((r) => {
          if (r && r.date) {
            map[String(r.date).slice(0, 10)] = String(r.status || '').toLowerCase();
          }
        });
        if (!cancelled) setCurrentMonthStatus(map);
      } catch (e) {
        console.error('Current month load failed:', e);
        if (!cancelled) setCurrentMonthStatus({});
      } finally {
        if (!cancelled) setCurrentMonthLoading(false);
      }
    };
    loadCurrent();
    return () => {
      cancelled = true;
    };
  }, [tc.id, currentMonthKey]);

  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const isCurrentMonth =
    month.getFullYear() === now.getFullYear() && month.getMonth() === now.getMonth();
  // For an in-progress month, only count days up to today; for past months, full month.
  const effectiveDays = isCurrentMonth ? now.getDate() : daysInMonth;

  const counts = useMemo(
    () => summarizeAttendance(statusByDate, effectiveDays),
    [statusByDate, effectiveDays]
  );

  const currentMonthDays = now.getDate();
  const currentMonthCounts = useMemo(
    () => summarizeAttendance(currentMonthStatus, currentMonthDays),
    [currentMonthStatus, currentMonthDays]
  );

  const notMarked = counts.notMarked;
  const attendanceRate = counts.attendanceRate;

  const [showSalaryMsg, setShowSalaryMsg] = useState(false);
  const [msgCopied, setMsgCopied] = useState(false);

  const salaryMessage = useMemo(() => {
    const totalDays = daysInMonth;
    const paidDays = counts.present + counts.halfday * 0.5;
    const amount = totalDays > 0 ? Math.round((DEFAULT_MONTHLY_SALARY / totalDays) * paidDays) : 0;
    const monthName = month.toLocaleString('default', { month: 'long' });
    const fmt = (n) => Number(n).toLocaleString('en-IN');
    return [
      `👤 Naam: ${tc.name}`,
      '',
      `Total Days: ${totalDays}`,
      `Present: ${counts.present} days + ${counts.halfday} Halfdays`,
      `Monthly Salary: ₹${fmt(DEFAULT_MONTHLY_SALARY)}`,
      '',
      '💰 Calculation:',
      `₹${fmt(DEFAULT_MONTHLY_SALARY)} ÷ ${totalDays} × ${paidDays} = ₹${fmt(amount)}`,
      `✅ ${monthName} ki Salary: ₹${fmt(amount)}`,
      '📲 Apna UPI QR ya UPI ID share karein — salary credit kar di jaayegi!',
    ].join('\n');
  }, [tc.name, month, daysInMonth, counts.present, counts.halfday]);

  const handleCopySalaryMsg = async () => {
    try {
      await navigator.clipboard.writeText(salaryMessage);
      setMsgCopied(true);
      setTimeout(() => setMsgCopied(false), 2000);
    } catch {
      /* ignore clipboard errors */
    }
  };

  const dateList = useMemo(() => {
    const list = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dt = new Date(dateStr);
      list.push({
        date: dateStr,
        day: d,
        weekday: dt.toLocaleDateString('en-US', { weekday: 'short' }),
        status: statusByDate[dateStr],
      });
    }
    return list;
  }, [month, daysInMonth, statusByDate]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-purple-600 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold truncate">{tc.name}</h2>
            {tc.tlName ? (
              <p className="text-sm text-white/80 mt-0.5 truncate">TL: {tc.tlName}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => {
                setMsgCopied(false);
                setShowSalaryMsg(true);
              }}
              disabled={loading}
              className="p-1.5 hover:bg-white/20 rounded-lg disabled:opacity-50"
              title="Salary message"
            >
              <MessageCircle size={22} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg"
              title="Close"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <button
            onClick={() => {
              const d = new Date(month);
              d.setMonth(d.getMonth() - 1);
              setMonth(d);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Previous month"
          >
            <ChevronLeft size={20} className="text-gray-700" />
          </button>
          <div className="font-semibold text-gray-900">{monthLabel(month)}</div>
          <button
            onClick={() => {
              const d = new Date(month);
              d.setMonth(d.getMonth() + 1);
              setMonth(d);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
            title="Next month"
          >
            <ChevronRight size={20} className="text-gray-700" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {loading ? (
            <div className="py-10 text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Loading…</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatBox
                  label="Present"
                  value={counts.present}
                  meta={STATUS_META.present}
                />
                <StatBox
                  label="Halfday"
                  value={counts.halfday}
                  meta={STATUS_META.halfday}
                />
                <StatBox
                  label="Absent"
                  value={counts.absent}
                  meta={STATUS_META.absent}
                />
                <StatBox
                  label="Office Leave"
                  value={counts.office_leave}
                  meta={STATUS_META.office_leave}
                />
                <StatBox
                  label="Not Marked"
                  value={notMarked}
                  meta={{ color: '#374151', bg: '#F3F4F6', border: '#E5E7EB' }}
                />
                <StatBox
                  label="Attendance %"
                  value={`${attendanceRate}%`}
                  meta={{
                    color: attendanceRate >= 80 ? '#059669' : attendanceRate >= 50 ? '#B45309' : '#B91C1C',
                    bg: attendanceRate >= 80 ? '#ECFDF5' : attendanceRate >= 50 ? '#FFFBEB' : '#FEF2F2',
                    border: attendanceRate >= 80 ? '#A7F3D0' : attendanceRate >= 50 ? '#FDE68A' : '#FCA5A5',
                  }}
                />
              </div>

              <div className="text-xs text-gray-500">
                Counted out of <span className="font-semibold text-gray-700">{effectiveDays}</span> day
                {effectiveDays === 1 ? '' : 's'}{isCurrentMonth ? ' (till today)' : ' (full month)'}.
                Attendance % = (Present + Halfday × 0.5) / Days.
              </div>

              {!isCurrentMonth && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-[11px] font-semibold text-gray-700">
                      Current month · {monthLabel(currentMonthStart)}
                    </p>
                    {currentMonthLoading ? (
                      <span className="text-[10px] text-gray-400">Loading…</span>
                    ) : (
                      <span
                        className="text-[11px] font-bold"
                        style={{
                          color:
                            currentMonthCounts.attendanceRate >= 80
                              ? '#059669'
                              : currentMonthCounts.attendanceRate >= 50
                                ? '#B45309'
                                : '#B91C1C',
                        }}
                      >
                        {currentMonthCounts.attendanceRate}%
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
                    <span>
                      <span className="font-semibold" style={{ color: STATUS_META.present.color }}>
                        {currentMonthCounts.present}
                      </span>{' '}
                      P
                    </span>
                    <span>
                      <span className="font-semibold" style={{ color: STATUS_META.halfday.color }}>
                        {currentMonthCounts.halfday}
                      </span>{' '}
                      H
                    </span>
                    <span>
                      <span className="font-semibold" style={{ color: STATUS_META.absent.color }}>
                        {currentMonthCounts.absent}
                      </span>{' '}
                      A
                    </span>
                    <span>
                      <span className="font-semibold" style={{ color: STATUS_META.office_leave.color }}>
                        {currentMonthCounts.office_leave}
                      </span>{' '}
                      L
                    </span>
                    <span>
                      <span className="font-semibold text-gray-700">{currentMonthCounts.notMarked}</span>{' '}
                      –
                    </span>
                    <span className="text-gray-400">till today ({currentMonthDays}d)</span>
                  </div>
                </div>
              )}

              {/* Calendar grid */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Daily breakdown</h3>
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 mb-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {/* leading blanks */}
                  {Array.from({ length: new Date(month.getFullYear(), month.getMonth(), 1).getDay() }).map((_, i) => (
                    <div key={`blank-${i}`} />
                  ))}
                  {dateList.map((d) => {
                    const meta = d.status ? STATUS_META[d.status] : null;
                    return (
                      <div
                        key={d.date}
                        className="aspect-square rounded border flex flex-col items-center justify-center text-xs font-semibold"
                        style={{
                          backgroundColor: meta ? meta.bg : '#FAFAFA',
                          color: meta ? meta.color : '#9CA3AF',
                          borderColor: meta ? meta.border : '#E5E7EB',
                        }}
                        title={`${d.date}${d.status ? ' — ' + (STATUS_META[d.status]?.label || d.status) : ' — Not Marked'}`}
                      >
                        <span className="text-[11px] leading-none">{d.day}</span>
                        <span className="text-[10px] leading-none mt-0.5">
                          {meta ? meta.short : '–'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 items-center pt-3 border-t border-gray-100">
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded font-bold text-[10px] border"
                      style={{ backgroundColor: m.bg, color: m.color, borderColor: m.border }}
                    >
                      {m.short}
                    </span>
                    <span className="text-gray-700">{m.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {showSalaryMsg && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setShowSalaryMsg(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Salary message</h3>
                <button
                  type="button"
                  onClick={() => setShowSalaryMsg(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                  title="Close"
                >
                  <X size={18} className="text-gray-600" />
                </button>
              </div>
              <div className="p-4">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto font-sans leading-relaxed">
                  {salaryMessage}
                </pre>
              </div>
              <div className="px-4 pb-4 flex gap-2">
                <button
                  type="button"
                  onClick={handleCopySalaryMsg}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
                >
                  {msgCopied ? <Check size={18} /> : <Copy size={18} />}
                  {msgCopied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSalaryMsg(false)}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, meta }) => (
  <div
    className="rounded-xl p-3 border"
    style={{ backgroundColor: meta.bg, borderColor: meta.border }}
  >
    <div className="text-xs font-semibold" style={{ color: meta.color }}>{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: meta.color }}>
      {value}
    </div>
  </div>
);

export default EmployeeAttendance;
